import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { Icon } from "../../../components/icons";
import { api } from "../../../lib/api";
import {
  DEMO_NEXT_TOURNAMENT_OPTIONS,
  DEMO_ORG_TYPE_OPTIONS,
  DEMO_REQUEST_LIMITS,
  DEMO_SPORT_OPTIONS,
  DEMO_TYPICAL_PLAYERS_OPTIONS,
  cleanMultiLine,
  cleanSingleLine,
  demoRequestContext,
  firstDemoRequestErrorField,
  firstName,
  mapDemoRequestError,
  normalizeDemoEmail,
  normalizeMxPhone,
  stripEdges,
  validateDemoRequest,
  type DemoOption,
  type DemoRequestField,
  type DemoRequestFieldErrors,
  type DemoRequestPayload,
  type DemoSport,
} from "../../../lib/demoRequest";
import { prefersReducedMotion } from "../hooks";
import "./DemoRequestForm.css";

type TextField = "full_name" | "phone" | "email" | "city" | "org_name" | "message";
type ChoiceField = "org_type" | "typical_players" | "next_tournament";

/** Lo que el visitante escribe, tal cual (se limpia al armar el envío). */
export interface DemoFormValues {
  full_name: string;
  phone: string;
  email: string;
  city: string;
  sports: DemoSport[];
  org_type: string | null;
  org_name: string;
  typical_players: string | null;
  next_tournament: string | null;
  message: string;
  consent: boolean;
  /** Trampa para robots: una persona nunca lo ve ni lo llena. */
  website: string;
}

function emptyValues(): DemoFormValues {
  return {
    full_name: "",
    phone: "",
    email: "",
    city: "",
    sports: [],
    org_type: null,
    org_name: "",
    typical_players: null,
    next_tournament: null,
    message: "",
    consent: false,
    website: "",
  };
}

/** Si el servidor no contesta en este tiempo, se corta y se trata como error
 * de red (no se queda en «Enviando…» para siempre). */
export const DEMO_SUBMIT_TIMEOUT_MS = 20_000;

/** Largo en caracteres reales (como `len()` de Python), para el contador. */
function charCount(value: string): number {
  return Array.from(value).length;
}

/** Opción elegida sin bordes invisibles, o null. */
function choice<T extends string>(value: string | null): T | null {
  return value ? ((stripEdges(value) || null) as T | null) : null;
}

/**
 * Cuerpo del POST: los datos limpios como los guarda el servidor (que de todos
 * modos vuelve a validar y limpiar todo) más los datos ocultos (página, UTM,
 * sitio de origen y tiempo en el formulario). La trampa va tal cual: el
 * servidor decide.
 */
export function buildDemoRequestPayload(
  values: DemoFormValues,
  context: { pathname: string; search: string; referrer: string | null; elapsedMs: number },
): DemoRequestPayload {
  return {
    full_name: cleanSingleLine(values.full_name),
    phone: normalizeMxPhone(values.phone) ?? stripEdges(values.phone),
    email: normalizeDemoEmail(values.email),
    city: cleanSingleLine(values.city),
    // Orden del catálogo y sin repetidos, como lo guarda el servidor.
    sports: DEMO_SPORT_OPTIONS.map((option) => option.value).filter((sport) => values.sports.includes(sport)),
    org_type: choice(values.org_type),
    org_name: cleanSingleLine(values.org_name) || null,
    typical_players: choice(values.typical_players),
    next_tournament: choice(values.next_tournament),
    message: cleanMultiLine(values.message) || null,
    consent: values.consent === true,
    website: values.website,
    ...demoRequestContext(context),
  };
}

/**
 * Formulario «Pedir demo» (spec B.6). Valida en el navegador con las reglas y
 * los mensajes de lib/demoRequest.ts (los mismos del servidor), envía con
 * `api.publicDemoRequests.create` y, al terminar, se sustituye por un mensaje
 * de gracias que recibe el foco.
 */
export function DemoRequestForm() {
  const uid = useId();
  const id = (name: string) => `${uid}-${name}`;

  const [values, setValues] = useState<DemoFormValues>(emptyValues);
  const [errors, setErrors] = useState<DemoRequestFieldErrors>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [sending, setSending] = useState(false);
  /** Aviso sobre el botón (sin conexión, límite de envíos…). */
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sentName, setSentName] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const successTitleRef = useRef<HTMLHeadingElement>(null);
  const mountedAt = useRef(Date.now());
  const sendingRef = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    mountedAt.current = Date.now();
    return () => {
      alive.current = false;
    };
  }, []);

  const succeeded = sentName !== null;

  useEffect(() => {
    if (!succeeded) return;
    successTitleRef.current?.focus({ preventScroll: true });
    successRef.current?.scrollIntoView?.({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }, [succeeded]);

  function focusField(field: DemoRequestField) {
    const target = formRef.current?.querySelector<HTMLElement>(`[name="${field}"]`);
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView?.({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }

  /** Vuelve a revisar un campo con los valores nuevos, solo si ya se intentó
   * enviar o si ese campo ya mostraba un error (no regaña mientras escribes). */
  function recheck(field: DemoRequestField, next: DemoFormValues, force = false) {
    setErrors((current) => {
      if (!force && !submitAttempted && !current[field]) return current;
      const message = validateDemoRequest(next)[field];
      if (message === current[field]) return current;
      const updated = { ...current };
      if (message) updated[field] = message;
      else delete updated[field];
      return updated;
    });
  }

  function change<K extends DemoRequestField | "website">(field: K, value: DemoFormValues[K]) {
    const next = { ...values, [field]: value };
    setValues(next);
    if (field !== "website") recheck(field as DemoRequestField, next);
  }

  function blurText(field: TextField) {
    if (stripEdges(values[field])) recheck(field, values, true);
  }

  function toggleSport(key: DemoSport) {
    const sports = values.sports.includes(key) ? values.sports.filter((sport) => sport !== key) : [...values.sports, key];
    change("sports", sports);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sendingRef.current) return;
    setSubmitAttempted(true);
    setSubmitError(null);

    const clientErrors = validateDemoRequest(values);
    setErrors(clientErrors);
    const firstInvalid = firstDemoRequestErrorField(clientErrors);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    const payload = buildDemoRequestPayload(values, {
      pathname: window.location.pathname,
      search: window.location.search,
      referrer: typeof document !== "undefined" ? document.referrer : null,
      elapsedMs: Date.now() - mountedAt.current,
    });
    sendingRef.current = true;
    setSending(true);
    const controller = typeof AbortController === "undefined" ? null : new AbortController();
    const timer = controller ? window.setTimeout(() => controller.abort(), DEMO_SUBMIT_TIMEOUT_MS) : null;
    let failure: unknown = null;
    try {
      await api.publicDemoRequests.create(payload, { signal: controller?.signal });
    } catch (error) {
      failure = error ?? new Error("Sin respuesta");
    } finally {
      if (timer !== null) window.clearTimeout(timer);
    }
    sendingRef.current = false;
    if (!alive.current) return;
    setSending(false);

    if (failure === null) {
      setSentName(firstName(values.full_name));
      return;
    }
    // 422 → mensaje del servidor en cada campo; 429 → su aviso; lo demás, red.
    const view = mapDemoRequestError(failure);
    if (view.kind === "validation") {
      setErrors(view.fieldErrors);
      const firstServerInvalid = firstDemoRequestErrorField(view.fieldErrors);
      if (firstServerInvalid) focusField(firstServerInvalid);
      return;
    }
    setSubmitError(view.message);
  }

  /** aria-describedby con la ayuda (si hay) y el error (si hay). */
  function describedBy(field: DemoRequestField, hasHelp: boolean): string | undefined {
    const ids = [hasHelp ? id(`${field}-help`) : null, errors[field] ? id(`${field}-error`) : null].filter(Boolean);
    return ids.length ? ids.join(" ") : undefined;
  }

  function fieldError(field: DemoRequestField): ReactNode {
    const message = errors[field];
    if (!message) return null;
    return (
      <p className="t123-demoform__error" id={id(`${field}-error`)}>
        <ErrorGlyph />
        {message}
      </p>
    );
  }

  function textField(
    field: TextField,
    label: string,
    options: {
      type?: "text" | "tel" | "email";
      autoComplete: string;
      inputMode?: "tel" | "email" | "text";
      autoCapitalize?: "words" | "off";
      maxLength: number;
      placeholder?: string;
      help?: string;
      full?: boolean;
    },
  ) {
    return (
      <div className={`t123-demoform__field${options.full ? " is-full" : ""}`}>
        <label className="t123-demoform__label" htmlFor={id(field)}>
          {label}
        </label>
        <input
          id={id(field)}
          className="t123-demoform__input"
          name={field}
          type={options.type ?? "text"}
          inputMode={options.inputMode}
          autoComplete={options.autoComplete}
          autoCapitalize={options.autoCapitalize}
          maxLength={options.maxLength}
          placeholder={options.placeholder}
          value={values[field]}
          onChange={(event) => change(field, event.target.value)}
          onBlur={() => blurText(field)}
          aria-invalid={errors[field] ? true : undefined}
          aria-describedby={describedBy(field, Boolean(options.help))}
          spellCheck={options.type === "email" || options.type === "tel" ? false : undefined}
        />
        {options.help ? (
          <p className="t123-demoform__help" id={id(`${field}-help`)}>
            {options.help}
          </p>
        ) : null}
        {fieldError(field)}
      </div>
    );
  }

  function choiceGroup(field: ChoiceField, legend: string, options: ReadonlyArray<DemoOption<string>>) {
    const invalid = Boolean(errors[field]);
    return (
      <fieldset className="t123-demoform__group is-full" aria-describedby={describedBy(field, false)}>
        <legend className="t123-demoform__label">{legend}</legend>
        <div className="t123-demoform__pills">
          {options.map((option) => (
            <label key={option.value} className="t123-demoform__pill">
              <input
                type="radio"
                name={field}
                value={option.value}
                checked={values[field] === option.value}
                onChange={() => change(field, option.value)}
                aria-invalid={invalid ? true : undefined}
              />
              <span className="t123-demoform__pill-face">
                <span className="t123-demoform__pill-check" aria-hidden="true">
                  <Icon name="check" />
                </span>
                {option.label}
              </span>
            </label>
          ))}
        </div>
        {fieldError(field)}
      </fieldset>
    );
  }

  const messageCount = charCount(values.message);

  return (
    <div className="t123-demoform">
      <div className="t123-demoform__live" aria-live="polite">
        {succeeded ? (
          <div className="t123-demoform__success" ref={successRef}>
            <span className="t123-demoform__success-icon" aria-hidden="true">
              <Icon name="check" />
            </span>
            <h3 className="t123-demoform__title" ref={successTitleRef} tabIndex={-1}>
              {sentName ? `¡Gracias, ${sentName}! Recibimos tu solicitud.` : "¡Gracias! Recibimos tu solicitud."}
            </h3>
            <p className="t123-demoform__success-copy">
              Te contactaremos por WhatsApp o correo para agendar tu demo. Mientras tanto, puedes ver la guía del
              organizador.
            </p>
            <div className="t123-demoform__success-actions">
              <Link to="/guia" className="t123-btn t123-btn--primary t123-btn--arrow">
                Ver la guía
              </Link>
              <Link to="/torneos" className="t123-btn t123-btn--outline t123-btn--arrow">
                Ver torneos abiertos
              </Link>
            </div>
          </div>
        ) : null}
      </div>

      {succeeded ? null : (
        <form
          ref={formRef}
          className="t123-demoform__form"
          noValidate
          onSubmit={handleSubmit}
          aria-labelledby={id("title")}
          aria-busy={sending || undefined}
        >
          <h3 className="t123-demoform__title" id={id("title")}>
            Solicita tu demo
          </h3>

          <div className="t123-demoform__grid">
            {textField("full_name", "Nombre y apellido", {
              autoComplete: "name",
              autoCapitalize: "words",
              maxLength: DEMO_REQUEST_LIMITS.nameMax,
              placeholder: "Ej. Laura Hernández",
            })}
            {textField("phone", "Celular (WhatsApp)", {
              type: "tel",
              inputMode: "tel",
              autoComplete: "tel-national",
              maxLength: 25,
              help: "A 10 dígitos. Te escribimos por aquí para agendar.",
            })}
            {textField("email", "Correo electrónico", {
              type: "email",
              inputMode: "email",
              autoComplete: "email",
              autoCapitalize: "off",
              maxLength: DEMO_REQUEST_LIMITS.emailMax,
              placeholder: "tu@correo.com",
            })}
            {textField("city", "Ciudad", {
              autoComplete: "address-level2",
              autoCapitalize: "words",
              maxLength: DEMO_REQUEST_LIMITS.cityMax,
              placeholder: "Ej. Querétaro, Qro.",
            })}

            <fieldset className="t123-demoform__group is-full" aria-describedby={describedBy("sports", true)}>
              <legend className="t123-demoform__label">Deporte</legend>
              <p className="t123-demoform__help t123-demoform__help--top" id={id("sports-help")}>
                Elige uno o varios.
              </p>
              <div className="t123-demoform__pills">
                {DEMO_SPORT_OPTIONS.map((option) => (
                  <label key={option.value} className="t123-demoform__pill">
                    <input
                      type="checkbox"
                      name="sports"
                      value={option.value}
                      checked={values.sports.includes(option.value)}
                      onChange={() => toggleSport(option.value)}
                      aria-invalid={errors.sports ? true : undefined}
                    />
                    <span className="t123-demoform__pill-face">
                      <span className="t123-demoform__pill-check" aria-hidden="true">
                        <Icon name="check" />
                      </span>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
              {fieldError("sports")}
            </fieldset>

            <div className="t123-demoform__divider is-full" aria-hidden="true">
              <span>Para preparar tu demo</span>
            </div>

            {choiceGroup("org_type", "¿Quién organiza? (opcional)", DEMO_ORG_TYPE_OPTIONS)}
            {textField("org_name", "Club u organización (opcional)", {
              autoComplete: "organization",
              maxLength: DEMO_REQUEST_LIMITS.orgNameMax,
              placeholder: "Ej. Club Arboleda",
              full: true,
            })}
            {choiceGroup("typical_players", "¿Cuántos jugadores tiene tu torneo típico? (opcional)", DEMO_TYPICAL_PLAYERS_OPTIONS)}
            {choiceGroup("next_tournament", "¿Cuándo es tu próximo torneo? (opcional)", DEMO_NEXT_TOURNAMENT_OPTIONS)}

            <div className="t123-demoform__field is-full">
              <label className="t123-demoform__label" htmlFor={id("message")}>
                ¿Qué te gustaría resolver? (opcional)
              </label>
              <textarea
                id={id("message")}
                className="t123-demoform__input t123-demoform__textarea"
                name="message"
                rows={4}
                maxLength={DEMO_REQUEST_LIMITS.messageMax}
                placeholder="Ej. cobrar inscripciones en línea y armar los cuadros más rápido."
                value={values.message}
                onChange={(event) => change("message", event.target.value)}
                onBlur={() => blurText("message")}
                aria-invalid={errors.message ? true : undefined}
                aria-describedby={describedBy("message", true)}
              />
              <p className="t123-demoform__counter" id={id("message-help")}>
                <span className="t123-sr-only">Llevas </span>
                {messageCount} de {DEMO_REQUEST_LIMITS.messageMax}
                <span className="t123-sr-only"> caracteres</span>
              </p>
              {fieldError("message")}
            </div>

            <div className="t123-demoform__field is-full">
              <div className={`t123-demoform__consent${errors.consent ? " is-invalid" : ""}`}>
                <input
                  id={id("consent")}
                  name="consent"
                  type="checkbox"
                  checked={values.consent}
                  onChange={(event) => change("consent", event.target.checked)}
                  aria-invalid={errors.consent ? true : undefined}
                  aria-describedby={describedBy("consent", false)}
                />
                <label htmlFor={id("consent")}>
                  Acepto el{" "}
                  <Link to="/aviso-de-privacidad" target="_blank" rel="noopener noreferrer">
                    Aviso de privacidad
                    <span className="t123-sr-only"> (se abre en otra pestaña)</span>
                  </Link>{" "}
                  y que Torneos123 me contacte sobre mi demo.
                </label>
              </div>
              {fieldError("consent")}
            </div>
          </div>

          {/* Trampa para robots: fuera de la vista, del teclado y del lector de
              pantalla. Si llega con algo, el servidor responde «ok» sin guardar. */}
          <div className="t123-demoform__trap" aria-hidden="true">
            <label htmlFor={id("website")}>Sitio web</label>
            <input
              id={id("website")}
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={values.website}
              onChange={(event) => change("website", event.target.value)}
            />
          </div>

          <div className="t123-demoform__alert" role="alert">
            {submitError ? (
              <p>
                <ErrorGlyph />
                {submitError}
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            className="t123-btn t123-btn--primary t123-demoform__submit"
            aria-disabled={sending || undefined}
            data-sending={sending || undefined}
          >
            {sending ? (
              <>
                <span className="t123-demoform__spinner" aria-hidden="true" />
                Enviando…
              </>
            ) : (
              "Pedir mi demo"
            )}
          </button>
          <p className="t123-demoform__note">
            <Icon name="lock" />
            Usamos estos datos solo para contactarte sobre tu demo. No los vendemos ni los usamos para publicidad.
          </p>
        </form>
      )}
    </div>
  );
}

/** Signo de alerta (círculo con «!») para errores; decorativo. */
function ErrorGlyph() {
  return (
    <svg
      className="t123-demoform__error-glyph"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 7.5v5.5M12 16.5v.01" />
    </svg>
  );
}
